import { distance as turfDistance, point as turfPoint } from '@turf/turf';
import { TurnInstruction } from '../turnInstructions';
import { DirectionRow, buildDirectionRows } from './directionsList';
import { ManeuverSign, isTerminal } from './maneuverSign';
import { RouteManeuver, Segment, parseInstructions, seamOffsets } from './plannerInstructionParser';
import { PointWithDistance, detectSeams } from './seamManeuverDetector';

/**
 * The turns a route being planned carries, in the shape the recording apps read them.
 *
 * The planner has the instructions before anything is saved — GraphHopper described each
 * segment as it was snapped, and they ride along on the line string. So the list can be shown
 * for a route that exists only on screen, and shows what the hiker will actually be told,
 * because it is the same arithmetic the phones run.
 *
 * Pure: line strings in, rows out. No store, no map.
 */

/** The minimum a line string has to expose for its turns to be placed. */
export interface DirectionsLineString {
  coordinates: number[][];
  instructions?: TurnInstruction[];
  profile?: string;
}

export function directionRowsFor(lineStrings: DirectionsLineString[]): DirectionRow[] {
  // Indexed before filtering, not after: `position` becomes the address an edit is written
  // back to, so it has to be the line string's own index in the route. A route holding a
  // segment with no geometry would otherwise renumber everything after it and send the edit
  // to the wrong line.
  //
  // A segment with no geometry has nothing to measure its turns against, and including it
  // would hand its share of the route to the segments around it.
  const usable = lineStrings
    .map((ls, index) => ({ ls, index }))
    .filter(({ ls }) => (ls.coordinates?.length ?? 0) > 0);
  if (usable.length === 0) return [];

  const points = pointsWithDistance(usable.flatMap(({ ls }) => ls.coordinates));
  if (points.length < 2) return [];
  const totalMeters = points[points.length - 1].distanceMeters;

  const segments: Segment[] = usable.map(({ ls, index }) => ({
    position: index,
    // The segment's own geometry rather than a stored distance: a hand-drawn segment
    // carries no distance at all, and a zero there would hand its share of the route to the
    // segments around it, moving their turns earlier than the junctions they describe.
    lengthMeters: geometryLength(ls.coordinates),
    instructions: ls.instructions ?? [],
    profile: ls.profile
  }));

  const fromInstructions = parseInstructions(segments, totalMeters);

  // Suppressed entries are still in `fromInstructions` here, deliberately: the detector skips
  // any seam that already has a maneuver within its separation window, so a hidden entry
  // written at a join is what stops a deleted turn being synthesised straight back.
  const seams = detectSeams(
    points,
    seamOffsets(segments, totalMeters),
    fromInstructions.map((m) => m.distanceFromStartMeters)
  ).map(
    (seam): RouteManeuver => ({
      distanceFromStartMeters: seam.distanceFromStartMeters,
      sign: seam.sign,
      text: '',
      isUserWorded: false,
      isHidden: false
    })
  );

  const visible = [...fromInstructions, ...seams]
    .filter((m) => !m.isHidden)
    .sort((a, b) => a.distanceFromStartMeters - b.distanceFromStartMeters);
  if (visible.length === 0) return [];

  // The route's end is known exactly, so take it rather than trusting the last instruction
  // to sit at it.
  const withFinish = [
    ...visible.filter((m) => !isTerminal(m.sign)),
    {
      distanceFromStartMeters: totalMeters,
      sign: ManeuverSign.FINISH,
      text: '',
      isUserWorded: false,
      isHidden: false
    } as RouteManeuver
  ];

  return buildDirectionRows(withFinish);
}

/** Whether a route has anything to list, so the tools entry can say so before opening. */
export function hasInstructions(lineStrings: DirectionsLineString[]): boolean {
  return lineStrings.some((ls) => (ls.instructions?.length ?? 0) > 0);
}

/**
 * Where on the route a row sits, as `[lng, lat]`.
 *
 * Measured over the same flattened vertices `directionRowsFor` positions turns against, so a
 * row's `distanceFromStartMeters` lands on the junction it describes.
 */
export function coordinateAtDistance(
  lineStrings: DirectionsLineString[],
  meters: number
): [number, number] | null {
  const points = pointsWithDistance(
    lineStrings.filter((ls) => (ls.coordinates?.length ?? 0) > 0).flatMap((ls) => ls.coordinates)
  );
  if (points.length === 0) return null;

  const target = Math.max(0, meters);
  for (let i = 1; i < points.length; i += 1) {
    const to = points[i];
    if (to.distanceMeters < target) continue;
    const from = points[i - 1];
    const span = to.distanceMeters - from.distanceMeters;
    const t = span > 0 ? (target - from.distanceMeters) / span : 0;
    return [
      from.coordinates[0] + (to.coordinates[0] - from.coordinates[0]) * t,
      from.coordinates[1] + (to.coordinates[1] - from.coordinates[1]) * t
    ];
  }
  const last = points[points.length - 1].coordinates;
  return [last[0], last[1]];
}

const APPROACH_WINDOW_METERS = 25;
const METERS_PER_DEGREE_LAT = 111_320;

/**
 * Compass bearing of the walk into a row, so its glyph can be turned to match the line.
 *
 * Port of Android's `RouteBearing.approaching`: a chord over the last 25 m rather than the
 * adjacent vertices, which wander by tens of degrees just before a junction. A row in the
 * opening metres has no approach, so it borrows the leg it leaves on.
 */
export function approachBearing(lineStrings: DirectionsLineString[], meters: number): number | null {
  const at = coordinateAtDistance(lineStrings, meters);
  if (!at) return null;

  if (meters >= APPROACH_WINDOW_METERS) {
    const from = coordinateAtDistance(lineStrings, meters - APPROACH_WINDOW_METERS);
    return from ? flatBearing(from, at) : null;
  }
  const ahead = coordinateAtDistance(lineStrings, meters + APPROACH_WINDOW_METERS);
  if (!ahead || (ahead[0] === at[0] && ahead[1] === at[1])) return null;
  return flatBearing(at, ahead);
}

function flatBearing(start: [number, number], end: [number, number]): number {
  const metersPerDegreeLon = METERS_PER_DEGREE_LAT * Math.cos((((start[1] + end[1]) / 2) * Math.PI) / 180);
  const east = (end[0] - start[0]) * metersPerDegreeLon;
  const north = (end[1] - start[1]) * METERS_PER_DEGREE_LAT;
  return ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360;
}

function geometryLength(coordinates: number[][]): number {
  let total = 0;
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const step = stepMeters(coordinates[i], coordinates[i + 1]);
    if (Number.isFinite(step) && step > 0) total += step;
  }
  return total;
}

/** Distance along the route at each vertex, measured the way the turns were positioned. */
function pointsWithDistance(coordinates: number[][]): PointWithDistance[] {
  const points: PointWithDistance[] = [];
  let cumulative = 0;

  coordinates.forEach((coordinate, index) => {
    if (index > 0) {
      const step = stepMeters(coordinates[index - 1], coordinate);
      if (Number.isFinite(step) && step > 0) cumulative += step;
    }
    points.push({ coordinates: coordinate, distanceMeters: cumulative });
  });
  return points;
}

function stepMeters(a: number[], b: number[]): number {
  return turfDistance(turfPoint(a), turfPoint(b), { units: 'meters' });
}
